/**
 * Service de notifications centralisé
 * Gère tous les types de notifications (toast, alert, etc.)
 */

class NotificationService {
  constructor() {
    this.toasts = [];
    this.listeners = [];
    this.nextId = 1;
  }

  /**
   * Ajouter un listener pour les notifications
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Notifier tous les listeners
   */
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.toasts));
  }

  /**
   * Afficher une notification toast
   */
  showToast(message, type = 'info', duration = 5000) {
    const toast = {
      id: this.nextId++,
      message,
      type,
      timestamp: Date.now(),
      duration
    };

    this.toasts.push(toast);
    this.notifyListeners();

    // Auto-remove après durée
    if (duration > 0) {
      setTimeout(() => {
        this.removeToast(toast.id);
      }, duration);
    }

    return toast.id;
  }

  /**
   * Supprimer une notification
   */
  removeToast(toastId) {
    this.toasts = this.toasts.filter(toast => toast.id !== toastId);
    this.notifyListeners();
  }

  /**
   * Vider toutes les notifications
   */
  clearAll() {
    this.toasts = [];
    this.notifyListeners();
  }

  /**
   * Méthodes de convenance pour différents types
   */
  success(message, duration = 5000) {
    return this.showToast(message, 'success', duration);
  }

  error(message, duration = 8000) {
    return this.showToast(message, 'error', duration);
  }

  warning(message, duration = 6000) {
    return this.showToast(message, 'warning', duration);
  }

  info(message, duration = 5000) {
    return this.showToast(message, 'info', duration);
  }

  /**
   * Notification de chargement
   */
  loading(message = 'Chargement...') {
    return this.showToast(message, 'loading', 0); // Pas d'auto-remove
  }

  /**
   * Mettre à jour une notification de chargement
   */
  updateLoading(toastId, message, type = 'success') {
    const toast = this.toasts.find(t => t.id === toastId);
    if (toast) {
      toast.message = message;
      toast.type = type;
      this.notifyListeners();
    }
  }

  /**
   * Gestionnaire d'erreurs API standardisé
   */
  handleApiError(error, context = '') {
    let message = 'Une erreur est survenue';
    
    // Log pour debug
    console.warn('handleApiError appelé avec:', { error, context, errorType: typeof error });
    
    // Vérifier que error existe et n'est pas null/undefined
    if (error && typeof error === 'object') {
      if (error.name === 'ApiError') {
        switch (error.status) {
          case 0:
            message = 'Erreur de connexion au serveur';
            break;
          case 401:
            message = 'Authentification requise';
            break;
          case 403:
            message = 'Accès refusé';
            break;
          case 404:
            message = 'Ressource non trouvée';
            break;
          case 500:
            message = 'Erreur serveur interne';
            break;
          default:
            message = error.message || `Erreur ${error.status}`;
        }
      } else if (error.message) {
        message = error.message;
      } else if (error.error && typeof error.error === 'string') {
        // Gestion des erreurs avec propriété 'error' imbriquée
        message = error.error;
      } else if (error.detail) {
        // Gestion des erreurs avec propriété 'detail'
        message = error.detail;
      } else {
        // Fallback pour les objets d'erreur sans propriétés connues
        message = 'Erreur inconnue';
      }
    } else if (typeof error === 'string') {
      // Si error est une chaîne de caractères
      message = error;
    } else if (error === null || error === undefined) {
      // Si error est null ou undefined
      message = 'Erreur inconnue';
    } else {
      // Fallback pour tout autre type
      message = 'Erreur inconnue';
    }

    if (context) {
      message = `${context}: ${message}`;
    }

    console.warn('Message d\'erreur final:', message);
    return this.error(message);
  }

  /**
   * Gestionnaire de succès API standardisé
   */
  handleApiSuccess(message, context = '') {
    const fullMessage = context ? `${context}: ${message}` : message;
    return this.success(fullMessage);
  }

  /**
   * Confirmation utilisateur
   */
  async confirm(message, title = 'Confirmation') {
    return new Promise((resolve) => {
      const confirmed = window.confirm(`${title}\n\n${message}`);
      resolve(confirmed);
    });
  }

  /**
   * Alert personnalisé
   */
  alert(message, title = 'Information') {
    window.alert(`${title}\n\n${message}`);
  }
}

// Instance singleton
const notificationService = new NotificationService();

// Export par défaut
export default notificationService;

// Exports nommés pour compatibilité.
//
// ⚠️ Ces méthodes s'appuient sur `this` (ex. handleApiError fait `this.error(...)`).
// Une simple déstructuration (`export const { handleApiError } = notificationService`)
// produisait des fonctions détachées : tout appel de l'export nommé levait
// « Cannot read properties of undefined (reading 'error') », donc AUCUNE notification
// d'erreur n'était affichée — un échec de sauvegarde des métadonnées restait
// silencieux, et l'appelant s'interrompait avant ses propres traitements.
// On lie donc explicitement chaque méthode au singleton.
const bind = (methodName) => notificationService[methodName].bind(notificationService);

export const showToast = bind('showToast');
export const success = bind('success');
export const error = bind('error');
export const warning = bind('warning');
export const info = bind('info');
export const loading = bind('loading');
export const updateLoading = bind('updateLoading');
export const removeToast = bind('removeToast');
export const clearAll = bind('clearAll');
export const addListener = bind('addListener');
export const handleApiError = bind('handleApiError');
export const handleApiSuccess = bind('handleApiSuccess');
export const confirm = bind('confirm');
export const alert = bind('alert');
